/**
 * Appearance provider.
 *
 * Owns the full look (theme, accent, font, size, corners, motion) and writes
 * it onto <html> before paint. The legacy `theme` localStorage key
 * (light | dark | system) is read once and stored as builder.appearance.v1.
 * index.html applies the same key before the bundle loads.
 */

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useState,
	type ReactNode,
} from 'react';
import {
	APPEARANCE_STORAGE_KEY,
	DEFAULT_APPEARANCE,
	applyAppearance,
	readAppearanceFromStorage,
	resolveTheme,
	type Appearance,
} from '../lib/appearance';

export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
	appearance: Appearance;
	resolvedTheme: ResolvedTheme;
	setAppearance: (patch: Partial<Appearance>) => void;
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function prefersDark(): boolean {
	if (typeof window === 'undefined' || !window.matchMedia) return false;
	return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function prefersReducedMotion(): boolean {
	if (typeof window === 'undefined' || !window.matchMedia) return false;
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function persistAppearance(appearance: Appearance) {
	try {
		window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
	} catch {
		// Quota or private mode: the look still applies for this session.
	}
}

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [appearance, setAppearanceState] = useState<Appearance>(() => readAppearanceFromStorage());
	const [systemDark, setSystemDark] = useState(prefersDark);
	const [systemReduce, setSystemReduce] = useState(prefersReducedMotion);

	useLayoutEffect(() => {
		applyAppearance(appearance, systemDark, systemReduce);
	}, [appearance, systemDark, systemReduce]);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		try {
			if (!window.localStorage.getItem(APPEARANCE_STORAGE_KEY)) {
				persistAppearance(appearance);
			}
		} catch {
			// Ignore unreadable storage.
		}
	}, [appearance]);

	useEffect(() => {
		if (appearance.theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
		const media = window.matchMedia('(prefers-color-scheme: dark)');
		const onChange = () => setSystemDark(media.matches);
		media.addEventListener('change', onChange);
		return () => media.removeEventListener('change', onChange);
	}, [appearance.theme]);

	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return;
		const media = window.matchMedia('(prefers-reduced-motion: reduce)');
		const onChange = () => setSystemReduce(media.matches);
		media.addEventListener('change', onChange);
		return () => media.removeEventListener('change', onChange);
	}, []);

	const setAppearance = useCallback((patch: Partial<Appearance>) => {
		setAppearanceState((current) => {
			const next = { ...current, ...patch };
			persistAppearance(next);
			return next;
		});
	}, []);

	// Header control flips the resolved family only. Accent, font, and radius stay.
	const toggleTheme = useCallback(() => {
		setAppearanceState((current) => {
			const dark = resolveTheme(current, prefersDark()).dark;
			const next = { ...current, theme: dark ? 'cream' : 'midnight' };
			persistAppearance(next);
			return next;
		});
	}, []);

	const resolvedTheme: ResolvedTheme = resolveTheme(appearance, systemDark).dark ? 'dark' : 'light';

	return (
		<ThemeContext.Provider value={{ appearance, resolvedTheme, setAppearance, toggleTheme }}>
			{children}
		</ThemeContext.Provider>
	);
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within ThemeProvider');
	}
	return context;
}

export { DEFAULT_APPEARANCE };
